import { FC, FunctionComponent, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'antd';
import cs from 'classnames';

import { Job } from 'types/jobs';

import { SubscriptionTypes } from 'constants/enums';

import { ReactComponent as CloseCircle } from 'assets/icons/close-circle.svg';
import { ReactComponent as Danger } from 'assets/icons/danger-filled.svg';
import { ReactComponent as Slash } from 'assets/icons/slash.svg';
import { ReactComponent as TickCircle } from 'assets/icons/tick-circle-filled.svg';

import styles from './JobSubscriptionStatus.module.scss';
import {
  SubscriptionIcons,
  mapSubscriptionToStatusUI
} from './mapSubscriptionToStatusUI';

interface JobSubscriptionStatusProps {
  job: Job;
}

const iconMap: Record<
  SubscriptionIcons,
  { Icon: FunctionComponent<SVGProps<SVGSVGElement>>; styles: string }
> = {
  ok: { Icon: TickCircle, styles: cs(styles.icon, styles.ok) },
  warn: { Icon: Danger, styles: cs(styles.icon, styles.warn) },
  close: { Icon: CloseCircle, styles: cs(styles.icon, styles.closed) },
  slash: { Icon: Slash, styles: cs(styles.icon, styles.failed) }
};

const JobSubscriptionStatus: FC<JobSubscriptionStatusProps> = ({
  job
}: JobSubscriptionStatusProps) => {
  const { t } = useTranslation();

  if (!job) {
    return null;
  }

  const { subscriptionType, subscriptionStatus, subscriptionDueDate } = job;

  const { iconType, text, tooltip } = mapSubscriptionToStatusUI(
    t,
    subscriptionType,
    subscriptionStatus,
    subscriptionDueDate
  );
  const iconConfig = iconType ? iconMap[iconType] : null;
  const StatusIcon = iconConfig?.Icon;
  const isFree = subscriptionType === SubscriptionTypes.Free;

  return (
    <div className={styles.wrap}>
      {StatusIcon && iconConfig && (
        <Tooltip title={tooltip} trigger="hover">
          <span className={styles.tooltip} role="img" aria-label={tooltip}>
            <StatusIcon className={iconConfig.styles} />
          </span>
        </Tooltip>
      )}
      <div className={cs(styles.text, { [styles.freeText]: isFree })}>
        {text}
      </div>
    </div>
  );
};

export default JobSubscriptionStatus;
